import React, { useState, useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../../lib/utils";

/**
 * Simple month calendar widget.
 * Highlights days that have events (from `events`: array of {date, kind, title}).
 */
export default function CalendarWidget({ events = [] }) {
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d;
  });

  const eventMap = useMemo(() => {
    const map = {};
    events.forEach((e) => {
      const d = new Date(e.date);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!map[key]) map[key] = [];
      map[key].push(e);
    });
    return map;
  }, [events]);

  const monthStart = new Date(cursor);
  const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
  const startWeekday = monthStart.getDay(); // 0..6, Sun-first
  const daysInMonth = monthEnd.getDate();
  const today = new Date();

  const grid = [];
  for (let i = 0; i < startWeekday; i++) grid.push(null);
  for (let d = 1; d <= daysInMonth; d++) grid.push(d);
  while (grid.length % 7 !== 0) grid.push(null);

  const monthLabel = cursor.toLocaleString(undefined, { month: "long", year: "numeric" });

  const kindColor = {
    audit: "bg-primary",
    task: "bg-amber-500",
    finding: "bg-red-500",
    default: "bg-primary",
  };

  return (
    <div data-testid="calendar-widget">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold">{monthLabel}</div>
        <div className="flex items-center gap-1">
          <button
            data-testid="calendar-prev"
            onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
            className="w-7 h-7 rounded hover:bg-muted flex items-center justify-center"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            data-testid="calendar-today"
            onClick={() => {
              const d = new Date();
              d.setDate(1);
              setCursor(d);
            }}
            className="h-7 px-2 rounded hover:bg-muted text-xs font-medium"
          >
            Today
          </button>
          <button
            data-testid="calendar-next"
            onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
            className="w-7 h-7 rounded hover:bg-muted flex items-center justify-center"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1 text-[11px] uppercase tracking-wider text-muted-foreground mb-1">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
          <div key={`dow-${i}`} className="text-center py-1">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {grid.map((d, i) => {
          if (d === null) return <div key={`blank-${i}`} className="h-9" />;
          const key = `${cursor.getFullYear()}-${cursor.getMonth()}-${d}`;
          const dayEvents = eventMap[key] || [];
          const isToday =
            today.getFullYear() === cursor.getFullYear() &&
            today.getMonth() === cursor.getMonth() &&
            today.getDate() === d;
          return (
            <div
              key={key}
              className={cn(
                "h-9 rounded-md text-xs flex flex-col items-center justify-center relative",
                isToday
                  ? "bg-primary text-primary-foreground font-semibold"
                  : "hover:bg-muted"
              )}
              title={dayEvents.map((e) => e.title).join("\n")}
            >
              <span>{d}</span>
              {dayEvents.length > 0 && (
                <span className="flex items-center gap-0.5 absolute bottom-0.5">
                  {dayEvents.slice(0, 3).map((e, idx) => (
                    <span
                      key={`${e.title || "ev"}-${idx}`}
                      className={cn(
                        "w-1 h-1 rounded-full",
                        kindColor[e.kind] || kindColor.default,
                        isToday && "bg-white"
                      )}
                    />
                  ))}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
